import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useLang } from "./LangContext";

export default function VideoFrame({ src, poster, badge, title, caption, width, height, testid }) {
  const { t } = useLang();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    video.defaultMuted = true;
    video.muted = true;
    video.loop = true;

    const tryPlay = () => {
      if (video.paused) video.play().catch(() => {});
    };
    const restart = () => {
      video.currentTime = 0;
      tryPlay();
    };
    tryPlay();
    video.addEventListener("loadeddata", tryPlay);
    video.addEventListener("canplay", tryPlay);
    video.addEventListener("pause", tryPlay);
    video.addEventListener("ended", restart);
    const gestureEvents = ["touchstart", "pointerdown", "keydown", "scroll"];
    gestureEvents.forEach((name) => window.addEventListener(name, tryPlay, { passive: true }));
    document.addEventListener("visibilitychange", tryPlay);
    return () => {
      video.removeEventListener("loadeddata", tryPlay);
      video.removeEventListener("canplay", tryPlay);
      video.removeEventListener("pause", tryPlay);
      video.removeEventListener("ended", restart);
      gestureEvents.forEach((name) => window.removeEventListener(name, tryPlay));
      document.removeEventListener("visibilitychange", tryPlay);
      video.pause();
    };
  }, [src]);

  const toggleSound = () => {
    const video = videoRef.current;
    video.muted = !video.muted;
    setMuted(video.muted);
    if (video.paused) video.play().catch(() => {});
  };

  const markFailed = () => {
    const video = videoRef.current;
    if (video && video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) setFailed(true);
  };

  const retry = () => {
    setFailed(false);
    videoRef.current.load();
    videoRef.current.play().catch(() => {});
  };

  return (
    <figure className="video-card" style={{ "--video-width": `${width}px` }} data-testid={testid}>
      <div className="frame video-frame" style={{ aspectRatio: `${width} / ${height}` }} data-testid={`${testid}-frame`}>
        <video ref={videoRef} poster={poster} width={width} height={height}
          autoPlay muted={muted} loop playsInline preload="auto" aria-label={caption}
          onVolumeChange={(event) => setMuted(event.currentTarget.muted)}
          onError={markFailed} onPlaying={() => setFailed(false)} data-testid={`${testid}-player`}>
          <source src={src} type="video/mp4" />
          <source src={src.replace(/\.mp4$/, ".webm")} type="video/webm" onError={markFailed} />
        </video>
        {badge && (
          <span className="video-badge" data-testid={`${testid}-badge`}>{badge}</span>
        )}
        <div className="video-controls">
          <button type="button" className="video-control" onClick={toggleSound} aria-pressed={!muted}
            aria-label={muted ? t.esports.soundOn : t.esports.soundOff} data-testid={`${testid}-sound-toggle`}>
            {muted ? <VolumeX size={16} aria-hidden="true" /> : <Volume2 size={16} aria-hidden="true" />}
          </button>
        </div>
      </div>
      <figcaption className="video-caption" data-testid={`${testid}-caption`}>
        {title && <strong className="font-display" data-testid={`${testid}-title`}>{title}</strong>}
        <span>{caption}</span>
      </figcaption>
      {failed && (
        <div className="video-error" role="alert" data-testid={`${testid}-error`}>
          <p>{t.esports.videoError}</p>
          <button type="button" className="btn btn-outline btn-sm" onClick={retry} data-testid={`${testid}-retry`}>
            {t.esports.retryVideo}
          </button>
        </div>
      )}
    </figure>
  );
}
